import { ChartData, ChartTypeRegistry } from 'chart.js';
import {
  ChartByStringBarScatter,
  DataPointSetByString,
  DataPointSetByStringYAxis
} from '../chart.models';
import { useMemo } from 'react';
import { chartColours } from 'common/components/charts/chart-colours';
import { ChartBarScatter } from 'common/components/charts/chart-bar-scatter';

export const ChartStrategyStringBarScatter = ({
  chart
}: {
  chart: ChartByStringBarScatter;
}) => {
  const groupByString = (
    barDataPointSets: DataPointSetByString[],
    scatterDataPointSets: DataPointSetByStringYAxis[]
  ) => {
    const labels = getLabels(barDataPointSets, scatterDataPointSets);

    const barDataSets = createBarDataSets(barDataPointSets);

    const scatterDataSets = createScatterDataSets(scatterDataPointSets);

    const datasets = appendColours(
      barDataPointSets,
      barDataSets,
      scatterDataPointSets,
      scatterDataSets
    );

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels,
      datasets
    };

    return chartData;
  };

  const groupedChartData = useMemo(() => {
    if (chart.barDataPointSets.length > 0 && chart.scatterDataPointSets) {
      return groupByString(chart.barDataPointSets, chart.scatterDataPointSets);
    }
  }, [chart.barDataPointSets, chart.scatterDataPointSets]);

  const tryRenderChart = () => {
    return (
      <ChartBarScatter
        title={chart.title}
        data={groupedChartData as ChartData<'bar'>}
        scatterDataPointSets={chart.scatterDataPointSets}
        yAxisFormat={chart.yAxisFormat}
        tooltipFormat={chart.tooltipFormat}
        xAxisTitle={chart.xAxisTitle}
        yAxisTitle={chart.yAxisTitle}
        yHighestValue={chart.yHighestValue}
        yScatterHighestValue={chart.yScatterHighestValue}
        key={`${chart.id}-bar-scatter`}
      />
    );
  };

  return groupedChartData ? tryRenderChart() : null;
};

function getLabels(
  dataPointSets: DataPointSetByString[],
  dataPointSetsOnCustomAxis: DataPointSetByStringYAxis[]
): string[] {
  const dataPointSetLabels = dataPointSets.map((dps) => dps.dataPointString);
  const dataPointSetOnCustomAxisLabels = dataPointSetsOnCustomAxis.map(
    (dps) => dps.dataPointString
  );

  return [
    ...new Set(dataPointSetLabels.concat(dataPointSetOnCustomAxisLabels))
  ];
}

function createBarDataSets(dataPointSets: DataPointSetByString[]): any[] {
  const dataPointSetNames = [
    ...new Set(dataPointSets.map((dps) => dps.dataPointSetName))
  ];

  return dataPointSetNames.map((dpsName) => {
    const dataPointSet = dataPointSets.find(
      (dps) => dps.dataPointSetName === dpsName
    );

    if (!dataPointSet) {
      throw Error(`Could not find bar dataPointSet with name ${dpsName}`);
    }

    const dataPointSetValues = dataPointSets
      .filter((dps) => dps.dataPointSetName === dpsName)
      .map((dps) => dps.value);

    const barDataSet: any = {
      label: dpsName,
      fill: true,
      data: dataPointSetValues,
      order: 2,
      categoryPercentage: 0.9,
      barPercentage: 1
    };

    return barDataSet;
  });
}

function createScatterDataSets(
  dataPointSets: DataPointSetByStringYAxis[]
): any[] {
  const dataPointSetNames = [
    ...new Set(dataPointSets.map((dps) => dps.dataPointSetName))
  ];

  return dataPointSetNames.map((dpsName) => {
    const dataPointSet = dataPointSets.find(
      (dps) => dps.dataPointSetName === dpsName
    );

    if (!dataPointSet) {
      throw Error(`Could not find scattter dataPointSet with name ${dpsName}`);
    }

    const dataPointSetValues = dataPointSets
      .filter((dps) => dps.dataPointSetName === dpsName)
      .map((dps) => dps.value);

    const scatterDataSet: any = {
      label: dpsName,
      fill: false,
      data: dataPointSetValues,
      type: 'scatter',
      pointRadius: 6,
      pointHoverRadius: 8,
      order: 1,
      yAxisID: dataPointSet.yAxisName
    };

    return scatterDataSet;
  });
}

function appendColours(
  barDataPointSets: DataPointSetByString[],
  barChartDataSets: any[],
  scatterDataPointSets: DataPointSetByStringYAxis[],
  scatterChartDataSets: any[]
): any[] {
  const allDataSetNames = barDataPointSets
    .map((s) => s.dataPointSetName)
    .concat(scatterDataPointSets.map((s) => s.dataPointSetName));

  const allDataSetNamesDistinct = [...new Set(allDataSetNames)];

  const combinedDataSets = barChartDataSets.concat(scatterChartDataSets);

  return combinedDataSets.map((dataSet) => {
    const colourIndex = allDataSetNamesDistinct.findIndex(
      (name) => name === dataSet.label
    );

    return {
      ...dataSet,
      backgroundColor: chartColours[colourIndex]
    };
  });
}
